import { m } from "@/paraglide/messages";
import { type Tool, tools } from "./index";

export type CategoryId =
  | "sports"
  | "science"
  | "dataviz"
  | "health"
  | "design"
  | "everyday"
  | "developer"
  | "text"
  | "calculators"
  | "education"
  | "generators"
  | "content";

export interface Category {
  id: CategoryId;
  name: () => string;
  description: () => string;
  icon: string;
}

/** Ordered category registry — display order on the homepage and in nav. */
export const CATEGORIES: Category[] = [
  {
    id: "sports",
    name: () => m.cat_sports_name(),
    description: () => m.cat_sports_desc(),
    icon: "🏆",
  },
  {
    id: "science",
    name: () => m.cat_science_name(),
    description: () => m.cat_science_desc(),
    icon: "🧪",
  },
  {
    id: "dataviz",
    name: () => m.cat_dataviz_name(),
    description: () => m.cat_dataviz_desc(),
    icon: "📊",
  },
  {
    id: "health",
    name: () => m.cat_health_name(),
    description: () => m.cat_health_desc(),
    icon: "❤️",
  },
  {
    id: "design",
    name: () => m.cat_design_name(),
    description: () => m.cat_design_desc(),
    icon: "🎨",
  },
  {
    id: "everyday",
    name: () => m.cat_everyday_name(),
    description: () => m.cat_everyday_desc(),
    icon: "🧰",
  },
  {
    id: "developer",
    name: () => m.cat_developer_name(),
    description: () => m.cat_developer_desc(),
    icon: "⚙️",
  },
  {
    id: "text",
    name: () => m.cat_text_name(),
    description: () => m.cat_text_desc(),
    icon: "✍️",
  },
  {
    id: "calculators",
    name: () => m.cat_calculators_name(),
    description: () => m.cat_calculators_desc(),
    icon: "🧮",
  },
  {
    id: "education",
    name: () => m.cat_education_name(),
    description: () => m.cat_education_desc(),
    icon: "🎓",
  },
  {
    id: "generators",
    name: () => m.cat_generators_name(),
    description: () => m.cat_generators_desc(),
    icon: "✨",
  },
  {
    id: "content",
    name: () => m.cat_content_name(),
    description: () => m.cat_content_desc(),
    icon: "📝",
  },
];

/**
 * Single source of truth: tool id → category. A tool absent from this map falls
 * back to "developer"; the category-completeness unit test asserts every tool
 * is mapped, so that fallback should never trigger in practice.
 */
export const TOOL_CATEGORY: Record<string, CategoryId> = {
  // Sports
  "f1-fantasy": "sports",
  // Science & Lab
  "solution-dilution": "science",
  "mass-molarity": "science",
  "molecular-weight": "science",
  "percent-solution": "science",
  normality: "science",
  "tm-calculator": "science",
  "dna-rna-molecular-weight": "science",
  "od260-concentration": "science",
  "reverse-complement": "science",
  "gc-content": "science",
  "gc-skew": "science",
  "phred-quality-score": "science",
  "iupac-to-regex": "science",
  "protein-translation": "science",
  "codon-usage": "science",
  rscu: "science",
  "g-force-calculator": "science",
  "pcr-master-mix-calculator": "science",
  "buffer-recipe-calculator": "science",
  "boiling-point-calculator": "science",
  "hplc-method-transfer": "science",
  // Charts & Data Visualization
  "bar-chart": "dataviz",
  "line-chart": "dataviz",
  "area-chart": "dataviz",
  "scatter-plot": "dataviz",
  "bubble-chart": "dataviz",
  "pie-chart": "dataviz",
  "donut-chart": "dataviz",
  histogram: "dataviz",
  "box-plot": "dataviz",
  heatmap: "dataviz",
  "radar-chart": "dataviz",
  "violin-plot": "dataviz",
  "ridgeline-plot": "dataviz",
  "venn-diagram": "dataviz",
  // Developer Tools
  "json-formatter": "developer",
  "base64-tool": "developer",
  "url-encoder": "developer",
  "hash-generator": "developer",
  "jwt-decoder": "developer",
  "uuid-inspector": "developer",
  "csv-json": "developer",
  "cron-explainer": "developer",
  "markdown-table": "developer",
  "number-base": "developer",
  "color-converter": "developer",
  "char-inspector": "developer",
  "text-to-binary": "developer",
  // Text & Writing
  "word-counter": "text",
  "case-converter": "text",
  "lorem-ipsum": "text",
  "find-replace": "text",
  "remove-duplicate-lines": "text",
  "text-repeat": "text",
  "line-sorter": "text",
  "text-reverse": "text",
  "whitespace-cleaner": "text",
  "word-frequency": "text",
  readability: "text",
  "syllable-counter": "text",
  "word-unscrambler": "text",
  "slug-generator": "text",
  "morse-code": "text",
  "text-diff": "text",
  // Calculators & Finance
  "percentage-calculator": "calculators",
  "loan-calculator": "calculators",
  "compound-interest": "calculators",
  "sip-calculator": "calculators",
  "age-calculator": "calculators",
  "date-difference": "calculators",
  "unit-converter": "calculators",
  "number-to-words": "calculators",
  "roman-numeral": "calculators",
  "data-size": "calculators",
  // Health & Fitness
  "bmi-calculator": "health",
  "bmr-calculator": "health",
  "macro-calculator": "health",
  "body-fat-calculator": "health",
  "ideal-weight-calculator": "health",
  "water-intake-calculator": "health",
  // Design & Color
  "aspect-ratio": "design",
  "contrast-checker": "design",
  "color-palette": "design",
  "ppi-calculator": "design",
  // Education & Math
  "gpa-calculator": "education",
  "fraction-calculator": "education",
  "statistics-calculator": "education",
  // Everyday & Measure
  ruler: "everyday",
  "qr-code-generator": "everyday",
  "countdown-timer": "everyday",
  "dice-roller": "everyday",
  "decision-wheel": "everyday",
  // Generators & Media
  "password-generator": "generators",
  "random-generator": "generators",
  "signature-maker": "generators",
  timer: "generators",
  "typing-speed-test": "generators",
  "youtube-thumbnail-downloader": "generators",
  // Content
  blog: "content",
};

export function categoryOf(toolId: string): CategoryId {
  return TOOL_CATEGORY[toolId] ?? "developer";
}

export interface CategorySection {
  category: Category;
  tools: Tool[];
}

/** Group the registry by category, in category order, registry order within. */
export function getToolsByCategory(): CategorySection[] {
  return CATEGORIES.map((category) => ({
    category,
    tools: tools.filter((t) => categoryOf(t.id) === category.id),
  })).filter((s) => s.tools.length > 0);
}
